import React, { useCallback, useEffect, useState } from 'react';
import {
  fetchPurchaseOrders,
  generatePurchaseOrder,
  approvePO,
  submitPO,
  InventoryApiError,
} from '../../services/inventoryApi';
import './PurchaseOrdersDashboard.css';

interface PurchaseOrdersDashboardProps {
  selectedStore: string;
  enterpriseId: string;
}

interface POLineItem {
  ingredientId?: string;
  ingredientName?: string;
  quantity?: number;
  unit?: string;
  unitCost?: number;
  lineTotal?: number;
}

interface PurchaseOrder {
  poId: string;
  status: string;
  supplierId?: string;
  supplierName?: string;
  total?: number;
  currency?: string;
  createdAt?: string;
  approvalThreshold?: number;
  requiresApproval?: boolean;
  lineItems?: POLineItem[];
}

type StatusFilter = 'all' | 'draft' | 'pending_approval' | 'approved' | 'submitted' | 'received';

const STATUSES: { id: StatusFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'draft', label: 'Draft' },
  { id: 'pending_approval', label: 'Pending Approval' },
  { id: 'approved', label: 'Approved' },
  { id: 'submitted', label: 'Submitted' },
  { id: 'received', label: 'Received' },
];

const formatMoney = (v?: number, currency = 'USD') => {
  if (v == null) return '-';
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(v);
  } catch {
    return `$${v.toFixed(2)}`;
  }
};

const PurchaseOrdersDashboard: React.FC<PurchaseOrdersDashboardProps> = ({
  selectedStore,
}) => {
  const [orders, setOrders] = useState<PurchaseOrder[]>([]);
  const [status, setStatus] = useState<StatusFilter>('all');
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!selectedStore) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetchPurchaseOrders(
        selectedStore,
        status === 'all' ? undefined : status
      );
      const list: PurchaseOrder[] = res?.purchaseOrders || res?.orders || res?.data || [];
      setOrders(list);
    } catch (e) {
      setError(e instanceof InventoryApiError ? e.message : 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, [selectedStore, status]);

  useEffect(() => {
    load();
  }, [load]);

  const handleGenerate = async () => {
    if (!selectedStore) return;
    setGenerating(true);
    setError(null);
    try {
      await generatePurchaseOrder(selectedStore, {});
      await load();
    } catch (e) {
      setError(e instanceof InventoryApiError ? e.message : 'Failed to generate orders');
    } finally {
      setGenerating(false);
    }
  };

  const handleApprove = async (poId: string) => {
    setBusyId(poId);
    setError(null);
    try {
      await approvePO(selectedStore, poId);
      await load();
    } catch (e) {
      setError(e instanceof InventoryApiError ? e.message : 'Failed to approve');
    } finally {
      setBusyId(null);
    }
  };

  const handleSubmit = async (poId: string) => {
    setBusyId(poId);
    setError(null);
    try {
      await submitPO(selectedStore, poId);
      await load();
    } catch (e) {
      setError(e instanceof InventoryApiError ? e.message : 'Failed to submit');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="po-dashboard">
      <div className="po-dashboard__header">
        <div>
          <h2>Purchase Orders</h2>
          <p className="po-dashboard__sub">
            Auto-generated and manual purchase orders by status
          </p>
        </div>
        <button
          className="btn-modern btn-modern-accent"
          onClick={handleGenerate}
          disabled={generating || !selectedStore}
        >
          {generating ? 'Generating...' : 'Generate Orders Now'}
        </button>
      </div>

      <div className="po-dashboard__filters">
        {STATUSES.map((s) => (
          <button
            key={s.id}
            className={`po-dashboard__chip${status === s.id ? ' po-dashboard__chip--active' : ''}`}
            onClick={() => setStatus(s.id)}
          >
            {s.label}
          </button>
        ))}
      </div>

      {error && <div className="po-dashboard__error">{error}</div>}

      {loading ? (
        <div className="po-dashboard__state">Loading purchase orders...</div>
      ) : orders.length === 0 ? (
        <div className="po-dashboard__state">No purchase orders in this status.</div>
      ) : (
        <div className="po-dashboard__list">
          {orders.map((po) => {
            const expanded = expandedId === po.poId;
            return (
              <div key={po.poId} className="po-card">
                <div className="po-card__row">
                  <div className="po-card__meta">
                    <span className={`po-card__status po-card__status--${po.status}`}>
                      {po.status.replace(/_/g, ' ')}
                    </span>
                    <strong>{po.supplierName || po.supplierId || 'Unknown Supplier'}</strong>
                    <span className="po-card__id">#{po.poId}</span>
                  </div>
                  <div className="po-card__total">
                    {formatMoney(po.total, po.currency)}
                  </div>
                </div>
                <div className="po-card__row po-card__row--compact">
                  <span>
                    {po.lineItems?.length || 0} line item{po.lineItems?.length === 1 ? '' : 's'}
                  </span>
                  {po.requiresApproval && po.approvalThreshold != null && (
                    <span className="po-card__threshold">
                      requires approval (&gt; {formatMoney(po.approvalThreshold, po.currency)})
                    </span>
                  )}
                </div>
                <div className="po-card__actions">
                  <button
                    className="btn-modern btn-modern-secondary"
                    onClick={() => setExpandedId(expanded ? null : po.poId)}
                  >
                    {expanded ? 'Hide' : 'View'} Items
                  </button>
                  {po.status === 'pending_approval' && (
                    <button
                      className="btn-modern btn-modern-accent"
                      disabled={busyId === po.poId}
                      onClick={() => handleApprove(po.poId)}
                    >
                      {busyId === po.poId ? 'Approving...' : 'Approve'}
                    </button>
                  )}
                  {(po.status === 'approved' || po.status === 'draft') && (
                    <button
                      className="btn-modern btn-modern-accent"
                      disabled={busyId === po.poId}
                      onClick={() => handleSubmit(po.poId)}
                    >
                      {busyId === po.poId ? 'Submitting...' : 'Submit to Supplier'}
                    </button>
                  )}
                </div>

                {expanded && po.lineItems && po.lineItems.length > 0 && (
                  <div className="po-card__lines">
                    <table>
                      <thead>
                        <tr>
                          <th>Ingredient</th>
                          <th>Qty</th>
                          <th>Unit</th>
                          <th>Unit Cost</th>
                          <th>Line Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {po.lineItems.map((line, i) => (
                          <tr key={i}>
                            <td>{line.ingredientName || line.ingredientId}</td>
                            <td>{line.quantity ?? '-'}</td>
                            <td>{line.unit || '-'}</td>
                            <td>{formatMoney(line.unitCost, po.currency)}</td>
                            <td>{formatMoney(line.lineTotal, po.currency)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PurchaseOrdersDashboard;
